/**
 * Hearen Premium Pricing Configuration
 * 
 * SINGLE SOURCE OF TRUTH for all Premium pricing in the frontend.
 * No hardcoded prices anywhere else in the codebase.
 * 
 * Target prices:
 * - Monthly: 7,90 € / month
 * - Yearly: 56,88 € / year (-40% vs monthly*12 = 94,80 €)
 * - Yearly monthly-equivalent: 4,74 € / month (56,88 / 12)
 * 
 * @version 2.0.0
 * @lastUpdated 2026-02-03
 */

export const PREMIUM_PRICING = {
  monthly: {
    price: '7,90€',
    priceEn: '€7.90',
    priceNumeric: 7.90,
    period: '/mois',
    periodEn: '/month',
    label: 'Mensuel',
    labelEn: 'Monthly',
    popular: false
  },
  yearly: {
    price: '56,88€',
    priceEn: '€56.88',
    priceNumeric: 56.88,
    period: '/an',
    periodEn: '/year',
    label: 'Annuel',
    labelEn: 'Yearly',
    // Monthly equivalent (56.88 / 12 = 4.74)
    monthlyEquivalent: '4,74€',
    monthlyEquivalentEn: '€4.74',
    monthlyEquivalentNumeric: 4.74,
    monthlyEquivalentPeriod: '/mois',
    monthlyEquivalentPeriodEn: '/mo',
    // Billing info
    billedYearly: 'facturé annuellement',
    billedYearlyEn: 'billed yearly',
    // Savings vs monthly
    save: 'Économisez 40%',
    saveEn: 'Save 40%',
    savePercent: 40,
    popular: true
  },
  // Comparison price (monthly * 12)
  monthlyYearlyTotal: {
    price: '94,80€',
    priceEn: '€94.80',
    priceNumeric: 94.80
  },
  launchOffer: {
    // Launch offer = same as yearly for now
    price: '56,88€',
    priceEn: '€56.88',
    priceNumeric: 56.88,
    regularPrice: '94,80€',
    regularPriceEn: '€94.80',
    regularPriceNumeric: 94.80,
    period: '/an',
    periodEn: '/year',
    discount: 40,
    title: '🎉 Offre de lancement',
    titleEn: '🎉 Launch offer',
    subtitle: 'économisez 40% la première année',
    subtitleEn: 'save 40% on your first year',
    description: 'Votre espace de calme profond, sans limites.',
    descriptionEn: 'Your space for deep calm, without limits.',
    badge: 'Offre limitée',
    badgeEn: 'Limited offer',
    // Monthly equivalent badge
    monthlyEquivalent: '4,74€',
    monthlyEquivalentEn: '€4.74',
    monthlyEquivalentPeriod: '/mois',
    monthlyEquivalentPeriodEn: '/mo'
  }
};

/**
 * Get formatted price based on language
 * @param {string} plan - 'monthly' or 'yearly'
 * @param {boolean} isEnglish - true for English format
 * @returns {string} Formatted price (e.g., "7,90€" or "€7.90")
 */
export const getFormattedPrice = (plan = 'monthly', isEnglish = false) => {
  const priceKey = isEnglish ? 'priceEn' : 'price';
  return PREMIUM_PRICING[plan]?.[priceKey] || PREMIUM_PRICING.monthly.price;
};

/**
 * Get formatted period based on language
 * @param {string} plan - 'monthly' or 'yearly'
 * @param {boolean} isEnglish - true for English format
 * @returns {string} Formatted period (e.g., "/mois" or "/month")
 */
export const getFormattedPeriod = (plan = 'monthly', isEnglish = false) => {
  const periodKey = isEnglish ? 'periodEn' : 'period';
  return PREMIUM_PRICING[plan]?.[periodKey] || PREMIUM_PRICING.monthly.period;
};

/**
 * Get yearly monthly equivalent display
 * @param {boolean} isEnglish - true for English format
 * @returns {object} { price, period, billedYearly }
 */
export const getYearlyMonthlyEquivalent = (isEnglish = false) => {
  const yearly = PREMIUM_PRICING.yearly;
  return {
    price: isEnglish ? yearly.monthlyEquivalentEn : yearly.monthlyEquivalent,
    period: isEnglish ? yearly.monthlyEquivalentPeriodEn : yearly.monthlyEquivalentPeriod,
    billedYearly: isEnglish ? yearly.billedYearlyEn : yearly.billedYearly
  };
};

/**
 * Calculate savings percentage (should always return 40)
 */
export const calculateYearlySavings = () => {
  return PREMIUM_PRICING.yearly.savePercent;
};

export default PREMIUM_PRICING;
